import React from "react";

// Common emoji shortcodes → actual emoji characters
const SHORTCODES = {
  smile: "😊", grin: "😁", joy: "😂", rofl: "🤣", lol: "😆",
  wink: "😉", blush: "☺️", heart_eyes: "😍", kissing: "😘", kissing_heart: "😘",
  yum: "😋", sunglasses: "😎", smirk: "😏", neutral: "😐", expressionless: "😑",
  unamused: "😒", roll_eyes: "🙄", grimacing: "😬", lying: "🤥", sleeping: "😴",
  mask: "😷", sick: "🤒", nauseated: "🤢", sneezing: "🤧", hot: "🥵",
  cold: "🥶", woozy: "🥴", dizzy_face: "😵", exploding_head: "🤯",
  cowboy: "🤠", partying: "🥳", nerd: "🤓", monocle: "🧐",
  worried: "😟", cry: "😢", sob: "😭", scream: "😱", angry: "😠",
  rage: "😡", skull: "💀", ghost: "👻", alien: "👽", robot: "🤖",
  thumbsup: "+1", thumbs_up: "👍", thumbsdown: "-1", thumbs_down: "👎",
  "+1": "👍", "-1": "👎", clap: "👏", wave: "👋", ok_hand: "👌",
  v: "✌️", crossed_fingers: "🤞", raised_hands: "🙌", pray: "🙏",
  point_up: "☝️", point_right: "👉", point_left: "👈", point_down: "👇",
  muscle: "💪", eyes: "👀", ear: "👂", nose: "👃", lips: "👄",
  heart: "❤️", broken_heart: "💔", orange_heart: "🧡", yellow_heart: "💛",
  green_heart: "💚", blue_heart: "💙", purple_heart: "💜", black_heart: "🖤",
  sparkling_heart: "💖", two_hearts: "💕", revolving_hearts: "💞",
  fire: "🔥", star: "⭐", sparkles: "✨", tada: "🎉", confetti: "🎊",
  party: "🥳", gift: "🎁", balloon: "🎈", trophy: "🏆", medal: "🥇",
  check: "✅", x: "❌", warning: "⚠️", stop: "🚫", hundred: "💯",
  thinking: "🤔", shrug: "🤷", facepalm: "🤦", no_mouth: "😶",
  hushed: "😯", astonished: "😲", open_mouth: "😮", flushed: "😳",
  sweat: "😓", disappointed: "😞", pensive: "😔", sleepy: "😪",
  relieved: "😌", confused: "😕", persevere: "😣", anguished: "😧",
  fearful: "😨", cold_sweat: "😰", weary: "😩", tired: "😫",
  dog: "🐶", cat: "🐱", mouse: "🐭", hamster: "🐹", rabbit: "🐰",
  bear: "🐻", panda: "🐼", fox: "🦊", lion: "🦁", cow: "🐮",
  pig: "🐷", frog: "🐸", monkey: "🐵", chicken: "🐔", penguin: "🐧",
  bird: "🐦", baby_chick: "🐤", duck: "🦆", eagle: "🦅", owl: "🦉",
  unicorn: "🦄", bee: "🐝", bug: "🐛", butterfly: "🦋", snail: "🐌",
  pizza: "🍕", hamburger: "🍔", fries: "🍟", hotdog: "🌭", taco: "🌮",
  sushi: "🍣", ramen: "🍜", cake: "🎂", cookie: "🍪", chocolate: "🍫",
  coffee: "☕", beer: "🍺", wine: "🍷", cocktail: "🍸",
  soccer: "⚽", basketball: "🏀", football: "🏈", baseball: "⚾",
  tennis: "🎾", trophy2: "🏆", game: "🎮", music: "🎵", notes: "🎶",
  sun: "☀️", moon: "🌙", rainbow: "🌈", cloud: "☁️", snow: "❄️",
  lightning: "⚡", umbrella: "☔", earth: "🌍", world: "🌎",
  car: "🚗", bus: "🚌", plane: "✈️", rocket: "🚀", phone: "📱",
  computer: "💻", email: "📧", book: "📚", pencil: "✏️", money: "💰",
  key: "🔑", lock: "🔒", bell: "🔔", mic: "🎤", camera: "📷",
};

const SHORTCODE_RE = /:([a-z0-9_+\-]+):/gi;

export function expandShortcodes(text) {
  return text.replace(SHORTCODE_RE, (match, code) => {
    return SHORTCODES[code.toLowerCase()] || match;
  });
}

// Strip all formatting for plain-text previews (e.g. chat list last message).
// Removes HTML tags, markdown syntax, and leading/trailing whitespace.
export function stripFormatting(text) {
  if (!text || typeof text !== "string") return text;
  return text
    .replace(/<[^>]+>/g, "")       // strip HTML tags: <u>, <b>, <br>, etc.
    .replace(/\*\*?([^*]+)\*\*?/g, "$1") // *bold* / **bold**
    .replace(/_{1,2}([^_]+)_{1,2}/g, "$1") // _italic_ / __italic__
    .replace(/~([^~]+)~/g, "$1")   // ~strikethrough~
    .replace(/`([^`]+)`/g, "$1")   // `code`
    .replace(/^>+\s?/gm, "")       // > blockquote
    .replace(/^[-*•]\s+/gm, "")    // - / * / • list items
    .replace(/^\d+\.\s+/gm, "")    // 1. ordered list
    .trim();
}

// ─── Chat message text parser ─────────────────────────────────────────────────
//
// Block-level (line-aware):
//   # Heading / ## Sub / ### Small       → h3/h4/h5 (scaled for bubble)
//   > quoted text                         → blockquote
//   - item / * item / • item             → <ul>
//   1. item / 2. item                    → <ol>
//   ```…```  (fenced code block)         → <pre><code>
//   <ul>…</ul>  <ol>…</ol>              → HTML lists
//   <br> / blank line                    → paragraph spacing
//
// Inline (within any line):
//   *bold*   _italic_   ~strike~   `code`
//   <b> <strong>  <i> <em>  <u>  <s> <strike>
//   https://…  http://…              → clickable link
//   @username                         → mention highlight

// ─── Constants ───────────────────────────────────────────────────────────────

const CODE_STYLE = {
  background: "rgba(128,128,128,0.2)",
  borderRadius: "3px",
  padding: "1px 4px",
  fontFamily: "monospace",
  fontSize: "0.9em",
};

const PRE_STYLE = {
  background: "rgba(128,128,128,0.15)",
  borderRadius: "6px",
  padding: "8px 10px",
  fontFamily: "monospace",
  fontSize: "0.85em",
  overflowX: "auto",
  margin: "4px 0",
  whiteSpace: "pre-wrap",
  wordBreak: "break-all",
};

const BLOCKQUOTE_STYLE = {
  borderLeft: "3px solid rgba(128,128,128,0.5)",
  margin: "4px 0",
  paddingLeft: "10px",
  opacity: 0.85,
  fontStyle: "italic",
};

const MENTION_STYLE = {
  color: "var(--mui-palette-primary-main, #1976d2)",
  fontWeight: 600,
};

const LINK_STYLE = {
  color: "var(--mui-palette-primary-main, #1976d2)",
  textDecoration: "underline",
  wordBreak: "break-all",
};

const HEADING_SIZES = { 1: "1.1em", 2: "1em", 3: "0.95em" };

// ─── Inline regex ─────────────────────────────────────────────────────────────

const INLINE_RE = new RegExp(
  [
    /\*([^*\n]+)\*/,                        // *bold*
    /_([^_\n]+)_/,                          // _italic_
    /~([^~\n]+)~/,                          // ~strike~
    /`([^`\n]+)`/,                          // `code`
    /<b>([\s\S]+?)<\/b>/i,                  // <b>
    /<i>([\s\S]+?)<\/i>/i,                  // <i>
    /<u>([\s\S]+?)<\/u>/i,                  // <u>
    /<s>([\s\S]+?)<\/s>/i,                  // <s>
    /<em>([\s\S]+?)<\/em>/i,               // <em>
    /<strong>([\s\S]+?)<\/strong>/i,        // <strong>
    /<strike>([\s\S]+?)<\/strike>/i,        // <strike>
    /(https?:\/\/[^\s<>"']+)/,             // URL
    /@([\w.]+)/,                            // @mention
  ].map(r => r.source).join("|"),
  "gi"
);

// ─── Block-level patterns ─────────────────────────────────────────────────────

const HEADING_RE = /^(#{1,3})\s+(.+)/;
const BLOCKQUOTE_RE = /^>\s?(.*)/;
const UL_RE = /^[-*•]\s+(.+)/;
const OL_RE = /^\d+\.\s+(.+)/;
const HTML_LIST_RE = /<(ul|ol)>([\s\S]*?)<\/(ul|ol)>/gi;
const HTML_LI_RE = /<li>([\s\S]*?)<\/li>/gi;
const HTML_BR_RE = /<br\s*\/?>/gi;
const FENCED_CODE_RE = /```([a-z]*)\n?([\s\S]*?)```/gi;

// ─── Inline parser ────────────────────────────────────────────────────────────

let _key = 0;

function parseInline(text) {
  if (!text) return null;
  INLINE_RE.lastIndex = 0;

  const nodes = [];
  let last = 0;
  let match;

  while ((match = INLINE_RE.exec(text)) !== null) {
    if (match.index > last) nodes.push(text.slice(last, match.index));

    const [full,
      mdBold, mdItalic, mdStrike, mdCode,
      htmlB, htmlI, htmlU, htmlS, htmlEm, htmlStrong, htmlStrike,
      url, mention,
    ] = match;
    const k = _key++;

    if      (mdBold     !== undefined) nodes.push(<strong key={k}>{mdBold}</strong>);
    else if (mdItalic   !== undefined) nodes.push(<em key={k}>{mdItalic}</em>);
    else if (mdStrike   !== undefined) nodes.push(<s key={k}>{mdStrike}</s>);
    else if (mdCode     !== undefined) nodes.push(<code key={k} style={CODE_STYLE}>{mdCode}</code>);
    else if (htmlB      !== undefined) nodes.push(<strong key={k}>{htmlB}</strong>);
    else if (htmlI      !== undefined) nodes.push(<em key={k}>{htmlI}</em>);
    else if (htmlU      !== undefined) nodes.push(<u key={k}>{htmlU}</u>);
    else if (htmlS      !== undefined) nodes.push(<s key={k}>{htmlS}</s>);
    else if (htmlEm     !== undefined) nodes.push(<em key={k}>{htmlEm}</em>);
    else if (htmlStrong !== undefined) nodes.push(<strong key={k}>{htmlStrong}</strong>);
    else if (htmlStrike !== undefined) nodes.push(<s key={k}>{htmlStrike}</s>);
    else if (url        !== undefined) nodes.push(
      <a key={k} href={url} target="_blank" rel="noopener noreferrer" style={LINK_STYLE}
         onClick={e => e.stopPropagation()}>{url}</a>
    );
    else if (mention    !== undefined) nodes.push(
      <span key={k} style={MENTION_STYLE}>@{mention}</span>
    );

    last = match.index + full.length;
  }

  if (last < text.length) nodes.push(text.slice(last));
  if (nodes.length === 0) return null;
  if (nodes.length === 1 && typeof nodes[0] === "string") return nodes[0];
  return nodes;
}

// ─── HTML list helper ─────────────────────────────────────────────────────────

function parseHtmlList(inner, Tag, k) {
  const items = [];
  HTML_LI_RE.lastIndex = 0;
  let m;
  while ((m = HTML_LI_RE.exec(inner)) !== null) {
    items.push(<li key={items.length}>{parseInline(m[1].trim())}</li>);
  }
  if (items.length === 0) {
    inner.split("\n").filter(Boolean).forEach((l, i) =>
      items.push(<li key={i}>{parseInline(l.trim())}</li>)
    );
  }
  return <Tag key={k} style={{ margin: "4px 0 4px 20px", padding: 0 }}>{items}</Tag>;
}

// ─── Main export ──────────────────────────────────────────────────────────────

// Wrap consecutive bare <li>…</li> lines (not already inside a list tag) into <ul>…</ul>
const BARE_LI_RE = /(<li>[\s\S]*?<\/li>(\s*<li>[\s\S]*?<\/li>)*)/gi;

export default function parseMessageText(text) {
  if (!text || typeof text !== "string") return text;
  _key = 0;
  text = expandShortcodes(text);
  // Wrap bare <li> sequences into <ul> so HTML_LIST_RE picks them up
  text = text.replace(BARE_LI_RE, (match) => `<ul>${match}</ul>`);

  const output = [];
  let k = 0;

  // 1. Extract fenced code blocks first (they span lines and must not be inline-parsed)
  const segments = [];
  let cursor = 0;
  FENCED_CODE_RE.lastIndex = 0;
  let fm;
  while ((fm = FENCED_CODE_RE.exec(text)) !== null) {
    if (fm.index > cursor) segments.push({ type: "text", content: text.slice(cursor, fm.index) });
    segments.push({ type: "code-block", content: fm[2] });
    cursor = fm.index + fm[0].length;
  }
  if (cursor < text.length) segments.push({ type: "text", content: text.slice(cursor) });

  for (const seg of segments) {
    if (seg.type === "code-block") {
      output.push(<pre key={k++} style={PRE_STYLE}><code>{seg.content.trimEnd()}</code></pre>);
      continue;
    }

    // 2. Inside text segments, extract HTML <ul>/<ol> blocks
    const textWithBr = seg.content.replace(HTML_BR_RE, "\n");
    const innerSegs = [];
    let ic = 0;
    HTML_LIST_RE.lastIndex = 0;
    let lm;
    while ((lm = HTML_LIST_RE.exec(textWithBr)) !== null) {
      if (lm.index > ic) innerSegs.push({ type: "text", content: textWithBr.slice(ic, lm.index) });
      innerSegs.push({ type: lm[1].toLowerCase() === "ul" ? "html-ul" : "html-ol", content: lm[2] });
      ic = lm.index + lm[0].length;
    }
    if (ic < textWithBr.length) innerSegs.push({ type: "text", content: textWithBr.slice(ic) });

    for (const is of innerSegs) {
      if (is.type === "html-ul") { output.push(parseHtmlList(is.content, "ul", k++)); continue; }
      if (is.type === "html-ol") { output.push(parseHtmlList(is.content, "ol", k++)); continue; }

      // 3. Line-by-line block parsing
      const lines = is.content.split("\n");
      let i = 0;

      while (i < lines.length) {
        const line = lines[i];
        const trimmed = line.trim();

        // Heading
        const hm = trimmed.match(HEADING_RE);
        if (hm) {
          const level = Math.min(hm[1].length, 3);
          output.push(
            <div key={k++} style={{ fontWeight: 700, fontSize: HEADING_SIZES[level], margin: "4px 0 2px" }}>
              {parseInline(hm[2])}
            </div>
          );
          i++; continue;
        }

        // Blockquote group
        if (BLOCKQUOTE_RE.test(trimmed)) {
          const lines_ = [];
          while (i < lines.length && BLOCKQUOTE_RE.test(lines[i].trim())) {
            const bm = lines[i].trim().match(BLOCKQUOTE_RE);
            lines_.push(bm[1]);
            i++;
          }
          output.push(
            <div key={k++} style={BLOCKQUOTE_STYLE}>
              {lines_.map((l, li) => <span key={li}>{parseInline(l)}{li < lines_.length - 1 && <br />}</span>)}
            </div>
          );
          continue;
        }

        // Unordered list group
        if (UL_RE.test(trimmed)) {
          const items = [];
          while (i < lines.length && UL_RE.test(lines[i].trim())) {
            const um = lines[i].trim().match(UL_RE);
            items.push(<li key={items.length}>{parseInline(um[1])}</li>);
            i++;
          }
          output.push(<ul key={k++} style={{ margin: "4px 0 4px 20px", padding: 0 }}>{items}</ul>);
          continue;
        }

        // Ordered list group
        if (OL_RE.test(trimmed)) {
          const items = [];
          while (i < lines.length && OL_RE.test(lines[i].trim())) {
            const om = lines[i].trim().match(OL_RE);
            items.push(<li key={items.length}>{parseInline(om[1])}</li>);
            i++;
          }
          output.push(<ol key={k++} style={{ margin: "4px 0 4px 20px", padding: 0 }}>{items}</ol>);
          continue;
        }

        // Empty line
        if (trimmed === "") {
          // Only add spacing if not at start or end
          if (i > 0 && i < lines.length - 1) output.push(<br key={k++} />);
          i++; continue;
        }

        // Regular text line — inline-parse and add line break between lines
        const parsed = parseInline(line);
        const isLast = i === lines.length - 1 || lines.slice(i + 1).every(l => l.trim() === "");
        output.push(<span key={k++}>{parsed}{!isLast && <br />}</span>);
        i++;
      }
    }
  }

  if (output.length === 0) return text;
  if (output.length === 1 && typeof output[0] === "string") return output[0];
  return output;
}
